/**
 * Pagina /admin/avisos — Sprint 13 S13-06 (migrada al portal vecino
 * bajo vista Comision desde apps/frontend-superadmin tras la
 * doctrina Portal vs Superadmin del 2026-05-13).
 *
 * Original: Sprint 12 S12-10. UI para publicar avisos al barrio. El
 * POST dispara fan-out sincrono a todos los vecinos activos con
 * email (S12-08). El modal muestra confirmacion con preview antes
 * de enviar.
 *
 * RBAC: requireRole='comision' + tipo_cuenta='operativa' (S13-01).
 */
import { useEffect, useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api/client';
import {
  crearAviso,
  listAvisosAdmin,
  retirarAviso,
} from '../../lib/api/avisos';
import type {
  AvisoCreadoResponse,
  AvisoRead,
  TipoAviso,
} from '../../lib/api/types';
import {
  ETIQUETA_TIPO_AVISO,
  TIPOS_AVISO,
} from '../../lib/api/types';
import { Button } from '../../components/ui/Button';
import { InputFecha } from '../../components/ui/InputFecha';

function formatFecha(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const [, anio, mes, dia] = m;
  return `${dia}-${mes}-${anio}`;
}

interface ModalNuevoProps {
  abierto: boolean;
  onCerrar: () => void;
  onCreado: (resultado: AvisoCreadoResponse) => void;
}

function ModalNuevo({ abierto, onCerrar, onCreado }: ModalNuevoProps) {
  const [titulo, setTitulo] = useState('');
  const [cuerpo, setCuerpo] = useState('');
  const [tipo, setTipo] = useState<TipoAviso>('informativo');
  const [vigenteHasta, setVigenteHasta] = useState('');
  const [confirmado, setConfirmado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!abierto) return null;

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!confirmado) {
      setError('Marca la confirmación antes de publicar.');
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      const resp = await crearAviso({
        titulo: titulo.trim(),
        cuerpo: cuerpo.trim(),
        tipo,
        vigente_hasta: vigenteHasta
          ? new Date(vigenteHasta).toISOString()
          : null,
      });
      onCreado(resp);
      setTitulo('');
      setCuerpo('');
      setTipo('informativo');
      setVigenteHasta('');
      setConfirmado(false);
    } catch (err) {
      const mensaje =
        err instanceof ApiError
          ? typeof err.body === 'object' && err.body && 'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`
          : 'Error de red';
      setError(mensaje);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onCerrar}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-nuevo-aviso-titulo"
    >
      <div
        className="bg-bg-elevated border-2 border-admin-red rounded-md p-6 max-w-xl w-full my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="modal-nuevo-aviso-titulo" className="text-xl font-medium text-text-primary mb-4">
          Publicar aviso
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="av-titulo" className="block text-sm font-medium text-text-primary mb-1">
              Título
            </label>
            <input
              id="av-titulo"
              type="text"
              required
              maxLength={200}
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
            />
          </div>
          <div>
            <label htmlFor="av-cuerpo" className="block text-sm font-medium text-text-primary mb-1">
              Cuerpo del aviso
            </label>
            <textarea
              id="av-cuerpo"
              required
              value={cuerpo}
              onChange={(e) => setCuerpo(e.target.value)}
              rows={6}
              placeholder="Texto que llegará al vecino por email + bell del portal."
              className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2 placeholder:text-text-muted"
            />
            <p className="text-xs text-text-muted mt-1">
              Separa los párrafos con líneas en blanco. El email mantiene esa
              estructura.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="av-tipo" className="block text-sm font-medium text-text-primary mb-1">
                Tipo
              </label>
              <select
                id="av-tipo"
                value={tipo}
                onChange={(e) => setTipo(e.target.value as TipoAviso)}
                className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
              >
                {TIPOS_AVISO.map((t) => (
                  <option key={t} value={t}>
                    {ETIQUETA_TIPO_AVISO[t]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="av-hasta" className="block text-sm font-medium text-text-primary mb-1">
                Vigente hasta (opcional)
              </label>
              <InputFecha
                id="av-hasta"
                value={vigenteHasta}
                onChange={setVigenteHasta}
                className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
              />
            </div>
          </div>

          <div className="bg-admin-red/[0.08] border-l-4 border-admin-red p-3 rounded-sm">
            <p className="text-sm text-text-primary mb-2">
              Al publicar, el aviso se envía por email a TODOS los vecinos
              activos con email y aparece en el bell del portal. Esta acción
              no se puede deshacer (solo se puede retirar del listado vigente).
            </p>
            <label className="flex items-center gap-2 text-sm text-text-primary cursor-pointer">
              <input
                type="checkbox"
                checked={confirmado}
                onChange={(e) => setConfirmado(e.target.checked)}
                className="rounded-sm accent-admin-red"
              />
              <span>Confirmo la publicación del aviso a todo el barrio.</span>
            </label>
          </div>

          {error && (
            <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary" role="alert">
              {error}
            </div>
          )}

          <div className="flex gap-3 justify-end pt-2">
            <Button type="button" variant="ghost" onClick={onCerrar}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={enviando || !confirmado}>
              {enviando ? 'Publicando…' : 'Publicar aviso'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function AvisosPage() {
  const [avisos, setAvisos] = useState<AvisoRead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [resultadoFanOut, setResultadoFanOut] = useState<AvisoCreadoResponse | null>(null);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError(null);
    listAvisosAdmin({ incluir_no_vigentes: true })
      .then((data) => {
        if (!cancelado) setAvisos(data);
      })
      .catch((err) => {
        if (cancelado) return;
        setError(
          err instanceof ApiError
            ? `Error ${err.status} al cargar avisos`
            : 'Error de red',
        );
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [refreshKey]);

  async function handleRetirar(aviso: AvisoRead) {
    if (!window.confirm(`¿Retirar "${aviso.titulo}" del listado del vecino?`))
      return;
    try {
      const ayer = new Date();
      ayer.setDate(ayer.getDate() - 1);
      await retirarAviso(aviso.id, ayer.toISOString());
      setRefreshKey((k) => k + 1);
    } catch (err) {
      const mensaje =
        err instanceof ApiError ? `Error ${err.status}` : 'Error de red';
      window.alert(`No pudimos retirar el aviso: ${mensaje}`);
    }
  }

  return (
    <div className="px-8 py-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-admin-red font-medium">
            Super admin · Acceso técnico
          </div>
          <h1 className="text-2xl font-medium text-text-primary mt-1">
            Avisos al barrio
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Cada aviso publicado se envía por email a todos los vecinos
            activos y aparece en el bell del portal.
          </p>
        </div>
        <Button variant="primary" onClick={() => setModalAbierto(true)}>
          + Nuevo aviso
        </Button>
      </div>

      {resultadoFanOut && (
        <div
          className="bg-state-success/15 border-l-4 border-state-success p-3 rounded-sm text-sm text-text-primary"
          role="status"
        >
          Aviso "<strong>{resultadoFanOut.aviso.titulo}</strong>" publicado.{' '}
          {resultadoFanOut.fan_out_emails_enviados} emails enviados,{' '}
          {resultadoFanOut.fan_out_emails_fallidos} fallaron,{' '}
          {resultadoFanOut.fan_out_notificaciones_creadas} notificaciones en el bell.{' '}
          <button
            type="button"
            onClick={() => setResultadoFanOut(null)}
            className="text-text-secondary underline ml-2"
          >
            Cerrar
          </button>
        </div>
      )}

      {loading && <p className="text-sm text-text-muted">Cargando…</p>}
      {error && !loading && (
        <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary">
          {error}
        </div>
      )}
      {!loading && !error && avisos.length === 0 && (
        <p className="text-sm text-text-muted">Aún no has publicado avisos.</p>
      )}
      {!loading && !error && avisos.length > 0 && (
        <div className="bg-bg-card border-[0.5px] border-white/[0.08] rounded-md overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b-[0.5px] border-white/15 bg-bg-sunken">
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-text-secondary font-medium">
                  Título
                </th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-text-secondary font-medium">
                  Tipo
                </th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-text-secondary font-medium">
                  Estado
                </th>
                <th className="text-right px-4 py-2 text-xs uppercase tracking-wide text-text-secondary font-medium">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {avisos.map((aviso) => {
                const ahora = new Date();
                const vigente =
                  aviso.vigente_hasta === null ||
                  new Date(aviso.vigente_hasta) >= ahora;
                return (
                  <tr
                    key={aviso.id}
                    className="border-b-[0.5px] border-white/[0.08]"
                    data-testid="fila-aviso"
                  >
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-text-primary">
                        {aviso.titulo}
                      </div>
                      <div className="text-xs text-text-muted mt-1">
                        Publicado {formatFecha(aviso.publicado_at.slice(0, 10))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary">
                      {ETIQUETA_TIPO_AVISO[aviso.tipo] ?? aviso.tipo}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {vigente ? (
                        <span className="inline-flex items-center px-2 py-[2px] bg-state-success/20 text-state-success rounded-xs text-xs font-medium">
                          Vigente
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-[2px] bg-bg-sunken text-text-muted rounded-xs text-xs">
                          Expirado
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {vigente && (
                        <button
                          type="button"
                          onClick={() => handleRetirar(aviso)}
                          className="text-sm text-state-danger hover:underline"
                        >
                          Retirar
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ModalNuevo
        abierto={modalAbierto}
        onCerrar={() => setModalAbierto(false)}
        onCreado={(resultado) => {
          setModalAbierto(false);
          setResultadoFanOut(resultado);
          setRefreshKey((k) => k + 1);
        }}
      />
    </div>
  );
}
